import { Activity, MAGIC_BUCKETS_ICON_URL, type Exercise } from '@/Common/ActivityFunctions';
import { ActivityTag, type Tag } from '@/Common/ActivityTag';
import { format_daymonth, format_time_of_day } from '@/time';

//@ts-ignore
const Xert = window.Xert;

/** The activity's owner, as the dashboard endpoints hand it over. */
export type ActivityOwner = {
  username?: string;
  first_name?: string;
  last_name?: string;
  profile_photo?: string;
  messaging_enabled?: boolean;
};
/** Training status entering an activity and recovery time leaving it -- see activityFitnessSummary(). */
export type ActivityFitnessSummary = {
  before?: {
    /** Training-level band, e.g. "Competitive". */
    cat?: string;
    /** 0-5, the same band as a number. */
    no?: number;
    /** Freshness, e.g. "Fresh" / "Very tired" / "Detraining". */
    form_cat?: string;
    color?: string;
    /** 0-1, how full the five-star training-level meter is. */
    ts_rating?: number;
    icon_color?: string;
  };
  /** Days until each energy system is recovered, from the state at the end of the activity. */
  recovery_days?: { low?: number; high?: number; peak?: number };
};

/**
 * The extra fields the dashboard endpoints append on top of a plain Exercise. Most are averages
 * and maxima the controller lifts out of the FIT session, plus the fitness context it derives.
 */
export type DashboardActivity = Exercise & {
  street_view?: string;
  user?: ActivityOwner;
  /** Squad dashboards scope links to /squads/{squad}/activity/{path}. */
  activity_url?: string;

  /** Server-rounded counterparts of avg_power / max_power. */
  avgPower?: number;
  maxPower?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  avg_heart_rate?: number;
  max_heart_rate?: number;
  avg_cadence?: number;
  max_cadence?: number;
  elevation?: number;
  /** Total work, in joules. */
  tw?: number;
  /** Grade-dependent power scaling exponent for the ride's terrain. */
  kge?: number;
  /** ~50 altitude samples across the ride, stored alongside kge -- see ActivityJob. */
  altitude_polyline?: number[];
  polarity_ratio?: string;
  externalLinks?: Partial<Record<'strava' | 'garmin', { text?: string; url: string }>>;
  fitness_summary?: ActivityFitnessSummary | null;
};

/**
 * Whether the viewer owns this activity. The coach and squad dashboards list other athletes'
 * activities, and none of the management actions (star, flag, delete, download) apply there --
 * the endpoints behind them are all scoped to the calling user.
 */
export function isOwnActivity(exercise: DashboardActivity): boolean {
  const owner = exercise?.user?.username;
  // The personal dashboard doesn't always carry an owner; everything in it is the viewer's.
  return !owner || owner === Xert?._username;
}

/** Squad cards link through the squad-scoped route; everything else goes to /activities/{path}. */
export function activityDetailsLink(exercise: DashboardActivity): string | null {
  return exercise?.activity_url ?? Activity.getDetailsPageLink(exercise);
}

/**
 * Comment bubbles follow the activity *owner's* messaging flag on the community dashboards (a
 * coach with messaging off still sees an enabled athlete's thread), and the viewer's own flag on
 * the personal dashboard, where the payload carries no messaging_enabled at all.
 */
export function activityMessagingEnabled(exercise: DashboardActivity): boolean {
  return exercise?.user?.messaging_enabled ?? Boolean(Xert?._hasMessagingFeature);
}

/**
 * The dashboard payload appends `street_view_url` unconditionally -- whether or not that thumbnail
 * was ever generated -- while hiding `chart_url`/`map_url` when their file is missing, and only
 * setting `street_view` once the controller has confirmed the thumbnail exists. So pick from the
 * fields that carry that guarantee; Activity.getOneImage() prefers `street_view_url` and would hand
 * back a 404 for most activities.
 */
export function activityImageUrl(exercise: DashboardActivity): string {
  return activityCardImages(exercise)[0]?.url ?? Activity.getBackupImageUrl();
}

/**
 * Which of the long card's four hand-built layouts to use, by the card's own width (not the
 * viewport's -- the same card is mounted full-page, inside the my-fitness tab, and on a phone).
 * Each tier lays its content out differently rather than reflowing the previous one:
 *   xs -- one column, image above, everything as labelled rows
 *   sm -- one column, image above, values in a two-up grid
 *   md -- one image beside the title, details across the full width underneath
 *   lg -- images beside the whole of the details, values in a single divided row
 */
export type CardLayout = 'xs' | 'sm' | 'md' | 'lg';

export function cardLayoutForWidth(width: number): CardLayout {
  if (width <= 0) return 'lg'; // pre-measurement: the common case, and avoids a visible reflow
  if (width < 480) return 'xs';
  // The image only needs ~240px beside the title, so the two-column top half is worth having well
  // before there is room for an image column beside the details as a whole.
  if (width < 640) return 'sm';
  if (width < 1020) return 'md';
  return 'lg';
}

/**
 * Which arrangement the details section itself gets. It matches the card's tier everywhere except
 * `md`, where the details run the full width of the card rather than sharing it with the image --
 * so from about the width the `lg` details column has, they can use the same single divided row
 * rather than the two-up grid the tier would otherwise imply.
 */
export function detailsLayoutForCard(layout: CardLayout, cardWidth: number): CardLayout {
  if (layout !== 'md') return layout;
  return cardWidth >= 860 ? 'lg' : 'md';
}

/** Which of the generated thumbnails this is; the card shows the type in its alt text. */
export type ActivityImage = { url: string; type: 'street_view' | 'map' | 'chart' | 'placeholder' | 'magic_buckets' };

/**
 * Every thumbnail this activity actually has, best first, for the card's image carousel. Same
 * guarantee as activityImageUrl(): only fields the controller has confirmed exist on disk.
 */
export function activityCardImages(exercise: DashboardActivity): ActivityImage[] {
  if (Activity.isMagicBuckets(exercise)) return [{ url: MAGIC_BUCKETS_ICON_URL, type: 'magic_buckets' }];
  const candidates: { url?: string; type: ActivityImage['type'] }[] = [
    { url: exercise?.street_view, type: 'street_view' },
    { url: exercise?.map_url, type: 'map' },
    { url: exercise?.chart_url, type: 'chart' },
  ];
  return candidates
    .filter((image): image is ActivityImage => Boolean(image.url))
    .filter((image, index, all) => all.findIndex((other) => other.url === image.url) === index);
}

/** "Monday, 5 May 2024 · 14:32", collapsing to "Today"/"Tomorrow" the way the rest of the app does. */
export function activityDateLabel(exercise: Exercise, now: Date): string {
  if (!exercise?.start_date) return '';
  const date = new Date(exercise.start_date);
  if (isNaN(date.getTime())) return '';
  const day = format_daymonth(date, now);
  const year = date.getFullYear() !== now.getFullYear() ? `, ${date.getFullYear()}` : '';
  return `${day}${year} · ${format_time_of_day(date, now)}`;
}

/**
 * Activities arrive from the dashboard endpoint without an `exerciseType`, which most of the
 * Activity helpers key off (a recorded activity also has a null `activity_mode`, so
 * Activity.isActivity() would say no). Stamp it on once, at the fetch boundary.
 */
export function asActivityExercise(raw: any): Exercise {
  return { ...raw, exerciseType: 'Activity' };
}

/**
 * The altitude samples worth drawing a profile from, or an empty array when there is no terrain to
 * show. An indoor ride reports either no altitude at all or the same number for the whole ride, and
 * a profile with no relief in it says nothing about where the athlete went -- so both count as
 * having none, which is also what takes elevation gain and KGE off the card.
 */
export function altitudeProfile(exercise: DashboardActivity): number[] {
  const points = (exercise?.altitude_polyline ?? []).filter(
    (value): value is number => typeof value === 'number' && isFinite(value)
  );
  if (points.length < 2) return [];
  // A metre of range is below what any real terrain produces.
  return Math.max(...points) - Math.min(...points) >= 1 ? points : [];
}

/** Whether this activity has terrain worth showing: a profile, and at least one number to read off it. */
export function hasTerrainToShow(exercise: DashboardActivity): boolean {
  if (!altitudeProfile(exercise).length) return false;
  return Boolean(exercise?.distance || exercise?.elevation || (exercise?.kge != null && isFinite(exercise.kge)));
}

export function activityCardTags(exercise: Exercise, size: 'default' | 'medium' | 'small'): Tag[] {
  const tag = ActivityTag(size);
  return [
    tag.exerciseType(exercise),
    tag.type(exercise),
    tag.powerSource(exercise),
    tag.device(exercise),
    tag.manual(exercise),
    tag.merged(exercise),
    tag.group(exercise),
    tag.duplicate(exercise),
    tag.hidden(exercise),
    tag.sharing(exercise),
  ].filter((t): t is Tag => t != null);
}
